import { useState, useEffect, SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getRoles } from '../../../../../redux/actions/roles'
import { getSeminarLessons } from '../../../../../redux/actions/seminarlessons'
import { getCountries, getFilteredCountries } from '../../../../../redux/actions/countries'
import { formValidator, resetFormValidator } from '../../../../../redux/actions/validator'
import { createNovelty, toggleFormNovelty } from '../../../../../redux/actions/novelties'
import { getLanguages } from '../../../../../redux/actions/languages'
import { IRootState } from '../../../../../redux/types'
import { INovelty } from '../../models/novelties'
import { IStatus, IVisibility } from '@/contexts/backoffice/courses/models/course'

export const useNoveltyModal = () => {
  const { t } = useTranslation(['global'])
  const dispatch = useDispatch()
  const { noveltySelected, showForm } = useSelector((state: IRootState) => state.novelty)
  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { isValidForm, errorForm, errorTab } = useSelector((state: IRootState) => state.validator)

  const STATUS: IStatus[] = [
    { id: 0, name: t('active') },
    { id: 1, name: t('inactive') },
    { id: 2, name: t('draft') }
  ]

  const VISIBILITY: IVisibility[] = [
    { id: 0, name: t('private') },
    { id: 1, name: 'VIP' },
    { id: 2, name: t('public') }
  ]

  const [noveltyState, setNoveltyState] = useState<INovelty>({
    id: null,
    brand: null,
    countries: [],
    languages: [],
    seminarlesson: null,
    creator: '',
    title: '',
    content: '',
    fileName: [],
    type: 0,
    status: STATUS[0],
    role: null,
    highlight: 0,
    visibility: VISIBILITY[0]
  })
  const [currentTab, setCurrentTab] = useState(0)
  const [msgCopy, setMsgCopy] = useState('')

  useEffect(() => {
    dispatch(getRoles())
    dispatch(getLanguages())
    dispatch(getSeminarLessons())

    if (authUser !== null) {
      if (authUser.role_id === 1) dispatch(getCountries())
      else dispatch(getFilteredCountries())
    }
  }, [dispatch, authUser])

  useEffect(() => {
    if (noveltySelected !== null) {
      setNoveltyState({
        id: noveltySelected.id,
        brand: noveltySelected.brand,
        countries: noveltySelected.countriesIds,
        languages: noveltySelected.languagesIds,
        seminarlesson: noveltySelected.seminarlesson,
        creator: noveltySelected.creator.name,
        title: noveltySelected.title,
        content: noveltySelected.content,
        fileName: [],
        type: noveltySelected.type,
        status: STATUS[noveltySelected.status],
        role: noveltySelected.role,
        highlight: noveltySelected.highlight,
        visibility: VISIBILITY[noveltySelected.visibility]
      })
    }
  }, [noveltySelected])

  useEffect(() => {
    if (isValidForm) {
      dispatch(
        createNovelty({
          ...noveltyState,
          typeNovelty: 'noveltySelected'
        })
      )
      handleCloseNoveltyModal()
    }
  }, [isValidForm, noveltyState, dispatch])

  const handleInputChange = (inputName: string, value: any) => {
    setNoveltyState({ ...noveltyState, [inputName]: value })
  }

  const handleChangeTab = (_: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }

  const handleCloseNoveltyModal = () => {
    dispatch(resetFormValidator())
    dispatch(toggleFormNovelty({ type: 'noveltySelected', change: false }))
  }

  const handleSubmit = () => {
    if (noveltyState.id === null) {
      dispatch(
        formValidator({
          countries: [noveltyState.countries, 'notempty', 'general'],
          languages: [noveltyState.languages, 'notempty', 'general'],
          title: [noveltyState.title, 'required|max:250|min:2'],
          content: [noveltyState.content, 'notempty', 'general'],
          fileName: [noveltyState.fileName, 'notempty', 'addOn'],
          role: [noveltyState.role, 'required', 'general']
        })
      )
    } else {
      dispatch(
        formValidator({
          id: [noveltyState.id, 'required'],
          countries: [noveltyState.countries, 'notempty', 'general'],
          languages: [noveltyState.languages, 'notempty', 'general'],
          title: [noveltyState.title, 'required|max:250|min:2'],
          content: [noveltyState.content, 'notempty', 'general'],
          role: [noveltyState.role, 'required', 'general']
        })
      )
    }
  }

  const handleCopyText = () => {
    navigator.clipboard.writeText('{mostrarSeminario}')
    setMsgCopy(t('text-copied-success'))
    setTimeout(() => {
      setMsgCopy('')
    }, 5000)
  }

  return {
    noveltyState,
    currentTab,
    msgCopy,
    showForm,
    errorForm,
    errorTab,
    isValidForm,
    handleInputChange,
    handleChangeTab,
    handleCloseNoveltyModal,
    handleSubmit,
    handleCopyText,
    STATUS,
    VISIBILITY
  }
}
